import { isRth } from './shareMinutes';

export interface VwapMinute {
  time: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface VwapPoint {
  time: number;
  value: number;
}

function typical(bar: VwapMinute): number {
  return (bar.high + bar.low + bar.close) / 3;
}

function rth(bars: readonly VwapMinute[]): VwapMinute[] {
  return bars.filter((bar) => isRth(bar.time) && bar.volume > 0);
}

/** Cumulative VWAP from `anchor` forward. Bars before the anchor are ignored. */
export function vwapFrom(bars: readonly VwapMinute[], anchor: number): VwapPoint[] {
  let sum = 0;
  let vol = 0;
  const out: VwapPoint[] = [];
  for (const bar of rth(bars)) {
    if (bar.time < anchor) continue;
    sum += typical(bar) * bar.volume;
    vol += bar.volume;
    out.push({ time: bar.time, value: sum / vol });
  }
  return out;
}

// One formatter, and one date per minute. Building a formatter per bar
// froze the chart for several seconds on every recompute.
const sessionFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const sessionCache = new Map<number, string>();

function sessionKey(ms: number): string {
  const minute = Math.floor(ms / 60_000);
  const cached = sessionCache.get(minute);
  if (cached) return cached;
  const key = sessionFormatter.format(new Date(ms));
  if (sessionCache.size > 100_000) sessionCache.clear();
  sessionCache.set(minute, key);
  return key;
}

function weekStart(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  const utc = new Date(Date.UTC(year, month - 1, date));
  const dow = utc.getUTCDay();
  const back = dow === 0 ? 6 : dow - 1;
  utc.setUTCDate(utc.getUTCDate() - back);
  return utc.toISOString().slice(0, 10);
}

const weekCache = new Map<string, string>();

/** Monday of the ET session's ISO week. One key for every day in that week. */
function isoWeek(ms: number): string {
  const day = sessionKey(ms);
  const cached = weekCache.get(day);
  if (cached) return cached;
  const start = weekStart(day);
  if (weekCache.size > 10_000) weekCache.clear();
  weekCache.set(day, start);
  return start;
}

function firstOfEach(bars: readonly VwapMinute[], keyOf: (ms: number) => string): Map<string, number> {
  const first = new Map<string, number>();
  for (const bar of rth(bars)) {
    const key = keyOf(bar.time);
    const prior = first.get(key);
    if (prior === undefined || bar.time < prior) first.set(key, bar.time);
  }
  return first;
}

export function dailyVwap(bars: readonly VwapMinute[]): VwapPoint[] {
  const opens = firstOfEach(bars, sessionKey);
  return stitch(bars, (time) => opens.get(sessionKey(time)) ?? time);
}

export function weeklyVwap(bars: readonly VwapMinute[]): VwapPoint[] {
  const opens = firstOfEach(bars, isoWeek);
  return stitch(bars, (time) => opens.get(isoWeek(time)) ?? time);
}

export function twoDayVwap(bars: readonly VwapMinute[]): VwapPoint[] {
  const traded = rth(bars);
  const keys = traded.map((bar) => sessionKey(bar.time));
  const first = new Map<string, number>();
  for (let i = 0; i < traded.length; i += 1) {
    const prior = first.get(keys[i]);
    if (prior === undefined || traded[i].time < traded[prior].time) first.set(keys[i], i);
  }
  const days = [...first.entries()].sort((a, b) => traded[a[1]].time - traded[b[1]].time);
  const out: VwapPoint[] = [];
  for (let day = 0; day < days.length; day += 1) {
    const key = days[day][0];
    const anchorIndex = day === 0 ? days[day][1] : days[day - 1][1];
    const anchorTime = traded[anchorIndex].time;
    let sum = 0;
    let vol = 0;
    for (let i = 0; i < traded.length; i += 1) {
      const bar = traded[i];
      if (bar.time < anchorTime) continue;
      sum += typical(bar) * bar.volume;
      vol += bar.volume;
      if (keys[i] === key) out.push({ time: bar.time, value: sum / vol });
    }
  }
  return out;
}

function stitch(bars: readonly VwapMinute[], anchorOf: (time: number) => number): VwapPoint[] {
  const out: VwapPoint[] = [];
  let anchor = Number.NaN;
  let sum = 0;
  let vol = 0;
  for (const bar of rth(bars)) {
    const next = anchorOf(bar.time);
    if (next !== anchor) {
      anchor = next;
      sum = 0;
      vol = 0;
    }
    if (bar.time < anchor) continue;
    sum += typical(bar) * bar.volume;
    vol += bar.volume;
    if (vol > 0) out.push({ time: bar.time, value: sum / vol });
  }
  return out;
}
